import { z } from 'zod';
import { kraPinSchema, moneySchema } from './common.schemas';
import { defineListQuery, listFilter } from './list-query.schemas';

export const BILLING_PREFERENCES = ['per_trip', 'weekly', 'monthly', 'per_po', 'per_vessel'] as const;
export type BillingPreference = (typeof BILLING_PREFERENCES)[number];

export const CUSTOMER_STATUSES = ['active', 'inactive'] as const;
export type CustomerStatus = (typeof CUSTOMER_STATUSES)[number];

export const billingAddressSchema = z.object({
  line1: z.string().trim().min(1).max(160),
  line2: z.string().trim().min(1).max(160).optional(),
  city: z.string().trim().min(1).max(80),
  country: z.string().trim().min(1).max(80),
});
export type BillingAddress = z.infer<typeof billingAddressSchema>;

/** `POST /customers` and `PATCH /customers/:id` — status changes go through their own `activate`/`deactivate` routes. */
export const createCustomerSchema = z.object({
  name: z.string().trim().min(1).max(160),
  kraPin: kraPinSchema,
  billingAddress: billingAddressSchema,
  paymentTermsDays: z.number().int().min(0).max(365),
  creditLimit: moneySchema,
  billingPreference: z.enum(BILLING_PREFERENCES),
  poNumberRequired: z.boolean(),
  /** A standing rate agreement (gitaumoses4/chukua#29) — lets a job for this customer skip Quoted, straight to Confirmed. */
  isContracted: z.boolean().default(false),
});
export type CreateCustomerInput = z.infer<typeof createCustomerSchema>;

export const updateCustomerSchema = createCustomerSchema;
export type UpdateCustomerInput = z.infer<typeof updateCustomerSchema>;

const customerContactFields = {
  name: z.string().trim().min(1).max(120),
  role: z.string().trim().min(1).max(80).optional(),
  phone: z.string().trim().min(1).max(32).optional(),
  email: z.email().optional(),
};

export const createCustomerContactSchema = z.object(customerContactFields);
export type CreateCustomerContactInput = z.infer<typeof createCustomerContactSchema>;

export const updateCustomerContactSchema = z.object(customerContactFields);
export type UpdateCustomerContactInput = z.infer<typeof updateCustomerContactSchema>;

/** `GET /customers` — search matches name and KRA PIN. */
export const customerListQuerySchema = defineListQuery({
  sortFields: ['name', 'createdAt'],
  defaultSort: { sort: 'name', order: 'asc' },
  filters: {
    status: listFilter(z.enum(CUSTOMER_STATUSES)),
  },
});
export type CustomerListQuery = z.infer<typeof customerListQuerySchema>;
